import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FaIcons from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { contentAPI, servicesAPI } from '../services/api';
import { useBooking } from '../contexts/BookingContext';
import { useSiteInfo } from '../contexts/SiteInfoContext';
import { normalizePhoneNumbers, getAddressLines } from '../utils/businessInfo';
import ServiceCard from '../components/services/ServiceCard';
import PromotionalBannerSlider from '../components/promotions/PromotionalBannerSlider';
import SpecialOffers from '../components/promotions/SpecialOffers';
import heroImage from '../assets/images/hero-main.jpg';
import './Home.css';

const { FaCheckCircle, FaMapMarkerAlt, FaPhoneAlt, FaRegCalendarCheck, FaStar, FaArrowRight, FaGoogle, FaFacebook, FaInstagram, FaEnvelope } = FaIcons;

const fallbackTestimonials = [
  {
    id: 'fallback-1',
    rating: 5,
    testimonial:
      'Outstanding experience. The Automotive Care team walked me through every recommendation and delivered on time.',
    customer_name: 'Samantha P.',
    verified: true,
  },
  {
    id: 'fallback-2',
    rating: 5,
    testimonial:
      'Professional, transparent, and great value. My car feels brand new after their major service.',
    customer_name: 'Mark D.',
    verified: true,
  },
];

const fallbackFeatures = [
  {
    id: 'integrity',
    title: 'Family-Owned Integrity',
    description: 'Transparent advice, honest pricing, and a team that treats your vehicle like our own.',
    icon: 'FaShieldAlt',
  },
  {
    id: 'efficiency',
    title: 'Efficient Turnaround',
    description: 'Smart scheduling and refined processes that get you back on the road faster.',
    icon: 'FaClock',
  },
  {
    id: 'quality',
    title: 'Quality Assured',
    description: 'OEM-grade parts and meticulous workmanship with every service and repair.',
    icon: 'FaCheckCircle',
  },
  {
    id: 'makes',
    title: 'All Makes & Models',
    description: 'Breadth of expertise across everyday runabouts, European marques, and commercial fleets.',
    icon: 'FaCar',
  },
];

const defaultMetrics = [
  { label: 'Established', value: '2017' },
  { label: 'Services Completed', value: '3K+' },
  { label: 'Customer Rated', value: '5★' },
];

const unwrapList = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const formatTime = (value) => {
  if (!value) {
    return '';
  }
  const [hourStr, minute] = value.split(':');
  let hour = parseInt(hourStr, 10);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${suffix}`;
};

const summariseHours = (hours) => {
  if (!hours) {
    return ['Mon-Thu & Sat: 9:30 AM - 5:30 PM', 'Friday & Sunday: Closed'];
  }

  const entries = Object.entries(hours).map(([day, info]) => {
    const label = day.charAt(0).toUpperCase() + day.slice(1);
    if (info.closed) {
      return `${label}: Closed`;
    }
    return `${label}: ${formatTime(info.open)} - ${formatTime(info.close)}`;
  });

  return entries;
};

const getIconComponent = (iconName) => {
  if (iconName && FaIcons[iconName]) {
    return FaIcons[iconName];
  }
  return FaCheckCircle;
};

const getPlatformInfo = (source) => {
  if (!source) {
    return { icon: FaCheckCircle, label: 'Verified', className: 'platform-verified' };
  }

  const sourceLower = source.toLowerCase();

  // Handle both new choice values and old string values
  if (sourceLower === 'google' || sourceLower.includes('google')) {
    return { icon: FaGoogle, label: 'Google Review', className: 'platform-google' };
  } else if (sourceLower === 'facebook' || sourceLower.includes('facebook')) {
    return { icon: FaFacebook, label: 'Facebook', className: 'platform-facebook' };
  } else if (sourceLower === 'instagram' || sourceLower.includes('instagram')) {
    return { icon: FaInstagram, label: 'Instagram', className: 'platform-instagram' };
  } else if (sourceLower === 'direct' || sourceLower.includes('direct')) {
    return { icon: FaCheckCircle, label: 'Direct Review', className: 'platform-direct' };
  } else if (sourceLower === 'email' || sourceLower.includes('email')) {
    return { icon: FaEnvelope, label: 'Email', className: 'platform-email' };
  }

  return { icon: FaCheckCircle, label: 'Review', className: 'platform-other' };
};

const Home = () => {
  const { openBookingModal } = useBooking();
  const { siteInfo } = useSiteInfo();

  const [heroBanners, setHeroBanners] = useState([]);
  const [homeFeatures, setHomeFeatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [homePageContent, setHomePageContent] = useState(null);
  const [loadingShowcase, setLoadingShowcase] = useState(true);
  const [showAllServices, setShowAllServices] = useState(false);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadContent = async () => {
      try {
        const [
          allServicesRes,
          servicesRes,
          testimonialsRes,
          categoriesRes,
          heroRes,
          featuresRes,
          promotionsRes,
        ] = await Promise.all([
          servicesAPI.getAllServices(),
          servicesAPI.getFeaturedServices(),
          servicesAPI.getFeaturedTestimonials(),
          servicesAPI.getCategories(),
          contentAPI.getHeroBanners(),
          contentAPI.getFeatures(),
          contentAPI.getPromotions(),
        ]);

        if (!isMounted) {
          return;
        }

        const allServicesList = unwrapList(allServicesRes.data);
        if (allServicesList.length) {
          setAllServices(allServicesList);
        }

        const featured = unwrapList(servicesRes.data);
        if (featured.length) {
          setFeaturedServices(featured);
        }

        const testimonialList = unwrapList(testimonialsRes.data);
        if (testimonialList.length) {
          setTestimonials(testimonialList);
        }

        const categoryList = unwrapList(categoriesRes.data);
        setCategories(categoryList);

        const heroList = unwrapList(heroRes.data);
        setHeroBanners(heroList.length > 0 ? heroList : []);

        const featureList = unwrapList(featuresRes.data);
        setHomeFeatures(featureList);

        const promotionList = unwrapList(promotionsRes.data);
        setPromotions(promotionList);
      } catch (error) {
        console.error('Failed to load homepage content', error);
      } finally {
        if (isMounted) {
          setLoadingShowcase(false);
        }
      }
    };

    const loadHomePageContent = async () => {
      try {
        const response = await contentAPI.getPage('home');
        if (isMounted) {
          setHomePageContent(response.data);
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Failed to load home page copy', error);
        }
      }
    };

    loadContent();
    loadHomePageContent();

    return () => {
      isMounted = false;
    };
  }, []);

  const businessInfo = useMemo(() => siteInfo?.business_info ?? {}, [siteInfo]);
  const company = useMemo(() => siteInfo?.company ?? {}, [siteInfo]);
  const tradingLines = useMemo(() => summariseHours(siteInfo?.hours), [siteInfo]);
  const contactPhones = useMemo(
    () => normalizePhoneNumbers(businessInfo, company),
    [businessInfo, company]
  );
  const addressLines = useMemo(
    () => getAddressLines(businessInfo, company),
    [businessInfo, company]
  );
  const metrics = useMemo(() => {
    const configured = siteInfo?.site_settings?.['home.metrics'];
    if (Array.isArray(configured) && configured.length) {
      return configured;
    }
    return defaultMetrics;
  }, [siteInfo]);

  const infoHighlights = useMemo(() => {
    const phoneNumbers = contactPhones;
    const primaryPhone = phoneNumbers[0]?.number || '0383763002';
    const phoneLine = phoneNumbers.length ? phoneNumbers[0]?.number : 'Call us';

    // Create Google Maps URL with address
    const address = addressLines.length > 0 ? addressLines[0] : '2A Supertron Court, Laverton North, VIC 3026';
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

    const addressDescription =
      addressLines.length > 0 ? addressLines : ['Laverton North, VIC 3026'];

    return [
      {
        key: 'address',
        icon: FaMapMarkerAlt,
        title: 'Our Location',
        lines: addressDescription,
        action: { type: 'external', href: mapsUrl, label: 'Directions' },
      },
      {
        key: 'phone',
        icon: FaPhoneAlt,
        title: 'Call Us',
        lines: [phoneLine],
        action: { type: 'external', href: `tel:${primaryPhone.replace(/\s+/g, '')}`, label: 'Call now' },
      },
      {
        key: 'hours',
        icon: FaRegCalendarCheck,
        title: 'Hours',
        lines: ['Mon-Thu & Sat: 9:30 AM - 5:30 PM'],
        action: { type: 'internal', to: '/contact', label: 'Contact Us' },
      },
    ];
  }, [contactPhones, addressLines]);

  const heroBadge =
    siteInfo?.site_settings?.['home.hero_badge'] || 'Family-Owned Since 2017';
  const primaryHeroBanner = heroBanners.length > 0 ? heroBanners[0] : null;
  const heroTitle = primaryHeroBanner?.title || 'Welcome to Automotive Care';
  const heroLead = primaryHeroBanner?.subtitle || 'Keeping your ride running on the road.';
  const heroDescription =
    primaryHeroBanner?.description ||
    'Premium servicing, diagnostics, and repairs delivered with transparent advice and genuine care in Laverton North.';

  const featuresToDisplay = homeFeatures.length ? homeFeatures : fallbackFeatures;

  // Display services with images (limit to 6 for initial view)
  const servicesToDisplay = allServices.slice(0, 6);

  const serviceSnapshot = featuredServices.length
    ? featuredServices.slice(0, 4).map((service) => ({
        key: service.slug,
        title: service.name,
        description: service.short_description,
        meta: service.display_price || 'Tailored quote available',
        to: `/services/${service.slug}`,
      }))
    : servicesToDisplay.slice(0, 4).map((service) => ({
        key: service.slug,
        title: service.name,
        description: service.short_description,
        meta: service.display_price || 'Tailored quote available',
        to: `/services/${service.slug}`,
      }));

  const ctaTitle =
    siteInfo?.site_settings?.['home.cta_title'] || 'Ready to Book Your Service?';
  const ctaDescription =
    siteInfo?.site_settings?.['home.cta_description'] ||
    'Reserve a time that works for you and our technicians will take care of the rest.';
  const ctaPrimaryLabel =
    siteInfo?.site_settings?.['home.cta_primary_label'] || 'Secure an appointment';
  const ctaSecondaryLabel =
    siteInfo?.site_settings?.['home.cta_secondary_label'] || 'Request a call back';

  const heroPrimaryLabel =
    siteInfo?.site_settings?.['home.hero_primary_label'] || 'Book Appointment';
  const heroSecondaryLabel =
    siteInfo?.site_settings?.['home.hero_secondary_label'] || 'Our Services';

  // Get featured promotions for slider
  const featuredPromotions = useMemo(() => {
    return promotions.filter(promo => promo.featured);
  }, [promotions]);

  return (
    <>
      <Helmet>
        <title>Automotive Care - Expert Car Service & Repairs | Laverton North</title>
        <meta
          name="description"
          content="Professional motor mechanic workshop in Laverton North. Expert car servicing, repairs, and maintenance at competitive prices. Book your appointment today!"
        />
      </Helmet>

      <section className="hero hero-with-slider">
        <div className="hero-slider-fullwidth">
          {heroBanners.length > 1 ? (
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={0}
              slidesPerView={1}
              navigation
              pagination={{
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 3
              }}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
              }}
              loop={true}
              speed={800}
              grabCursor={true}
              keyboard={{
                enabled: true,
                onlyInViewport: true
              }}
              className="hero-fullwidth-swiper"
            >
              {heroBanners.map((banner, index) => (
                <SwiperSlide key={banner.id || index}>
                  <div className="hero-slide">
                    <img
                      src={banner.background_image}
                      alt={banner.title || 'Automotive Care Workshop'}
                      className="hero-slide-image"
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      fetchpriority={index === 0 ? "high" : "auto"}
                    />
                    <div className="hero-slide-overlay" />
                    <div className="container hero-slide-content">
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                      >
                        <span className="hero-badge">{heroBadge}</span>
                        <h1 className="hero-title">{banner.title || heroTitle}</h1>
                        <p className="hero-lead">{banner.subtitle || heroLead}</p>
                        <p className="hero-subtitle">{banner.description || heroDescription}</p>
                        <div className="hero-buttons">
                          <button type="button" className="btn btn-primary btn-lg" onClick={openBookingModal}>
                            {heroPrimaryLabel}
                          </button>
                          <Link to="/services" className="btn btn-outline btn-lg">
                            {heroSecondaryLabel}
                          </Link>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="hero-slide">
              <img
                src={
                  heroBanners.length === 1
                    ? heroBanners[0].background_image
                    : homePageContent?.hero_image
                    ? homePageContent.hero_image
                    : heroImage
                }
                alt="Automotive Care Workshop"
                className="hero-slide-image"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
              <div className="hero-slide-overlay" />
              <div className="container hero-slide-content">
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <span className="hero-badge">{heroBadge}</span>
                  <h1 className="hero-title">{heroTitle}</h1>
                  <p className="hero-lead">{heroLead}</p>
                  <p className="hero-subtitle">{heroDescription}</p>
                  <div className="hero-buttons">
                    <button type="button" className="btn btn-primary btn-lg" onClick={openBookingModal}>
                      {heroPrimaryLabel}
                    </button>
                    <Link to="/services" className="btn btn-outline btn-lg">
                      {heroSecondaryLabel}
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </div>

        <div className="container">
          <motion.div
            className="hero-info-grid"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {infoHighlights.map((item) => {
              const Icon = item.icon;
              return (
                <div className="hero-info-card" key={item.key}>
                  <div className="hero-info-icon">
                    <Icon />
                  </div>
                  <div className="hero-info-content">
                    <h4>{item.title}</h4>
                    {item.lines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                    {item.action && item.action.type === 'internal' && (
                      <Link to={item.action.to} className="hero-info-link">
                        {item.action.label}
                      </Link>
                    )}
                    {item.action && item.action.type === 'external' && (
                      <a href={item.action.href} className="hero-info-link">
                        {item.action.label}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {featuredPromotions.length > 0 && (
        <PromotionalBannerSlider promotions={promotions} />
      )}

      <section className="section about-preview">
        <div className="container">
          <div className="about-preview-grid">
            <motion.div
              className="about-preview-text"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>{company.name || 'Keeping You and Your Car Safely on the Road'}</h2>
              {homePageContent?.content ? (
                <div
                  className="rich-text"
                  dangerouslySetInnerHTML={{ __html: homePageContent.content }}
                />
              ) : (
                <>
                  <p>
                    Automotive Care is a family-owned and operated motor mechanic workshop located in
                    Laverton North. Our mission is to service and repair vehicles at competitive prices
                    while upholding the highest standards of quality, transparency, and honesty.
                  </p>
                  <p>
                    From routine servicing to complex repairs, our experienced team has you covered with
                    workmanship you can trust and customer service that feels personal.
                  </p>
                  <ul>
                    <li>Minor, major, and logbook servicing</li>
                    <li>Brakes, suspension, tyres, and battery replacements</li>
                    <li>Diagnostics, inspections, and air-conditioning expertise</li>
                  </ul>
                </>
              )}
              <Link to="/about" className="about-preview-link">
                Learn more about us
              </Link>
            </motion.div>

            <motion.div
              className="about-preview-panel"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="panel-highlight">
                <strong>Laverton North Specialists</strong>
                <span>Trusted by local motorists and fleet operators alike.</span>
              </div>
              <div className="panel-divider" />
              <div className="panel-metrics">
                {metrics.map((metric) => (
                  <div key={metric.label}>
                    <span className="metric-value">{metric.value}</span>
                    <span className="metric-label">{metric.label}</span>
                  </div>
                ))}
              </div>
              <p className="panel-note">
                Reliable workmanship, genuine customer service, and exceptional value are the promises we
                stand by every day.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section signature-services">
        <div className="container">
          <motion.div
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Services</h2>
            <p className="section-subtitle">
              Comprehensive automotive care tailored to your vehicle&apos;s needs.
            </p>
          </motion.div>

          <div className="services-grid">
            {(showAllServices ? allServices : servicesToDisplay).map((service, index) => {
              const Icon = getIconComponent(service.category?.icon);
              return (
                <ServiceCard
                  key={service.id || service.slug}
                  category={service}
                  index={index}
                  onBookClick={openBookingModal}
                  Icon={Icon}
                />
              );
            })}
          </div>

          {!showAllServices && allServices.length > servicesToDisplay.length && (
            <div className="text-center" style={{ marginTop: '2.5rem' }}>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={() => setShowAllServices(true)}
              >
                See All Services <FaArrowRight />
              </button>
            </div>
          )}

          {showAllServices && (
            <div className="text-center" style={{ marginTop: '2.5rem' }}>
              <button
                type="button"
                className="btn btn-outline btn-lg"
                onClick={() => setShowAllServices(false)}
              >
                Show Less
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="section features-section">
        <div className="container">
          <motion.div
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Why Drivers Choose Automotive Care</h2>
            <p className="section-subtitle">
              Experience a workshop dedicated to precision, transparency, and long-term value.
            </p>
          </motion.div>

          <div className="features-grid">
            {featuresToDisplay.map((feature, index) => {
              const FeatureIcon = getIconComponent(feature.icon);
              return (
                <motion.div
                  key={feature.id || feature.title}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ translateY: -10 }}
                >
                  <div className="feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {promotions.length > 0 && <SpecialOffers promotions={promotions} />}

      <section className="section featured-services">
        <div className="container">
          <motion.div
            className="featured-services-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="featured-services-header">
              <h2>Popular Service Requests</h2>
              <p>
                Whether you need diagnostics, scheduled maintenance, or specialist repairs, we tailor the experience to your
                vehicle.
              </p>
            </div>
            <div className="featured-services-list">
              {loadingShowcase ? (
                <div className="spinner" />
              ) : (
                serviceSnapshot.map((service) => (
                  <Link to={service.to} className="featured-service-item" key={service.key}>
                    <div>
                      <h4>{service.title}</h4>
                      <p>{service.description}</p>
                    </div>
                    <span className="featured-service-meta">{service.meta}</span>
                  </Link>
                ))
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section testimonials-section">
        <div className="container">
          <motion.div
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>What Our Customers Say</h2>
            <p className="section-subtitle">Real feedback from the drivers we look after each day.</p>
          </motion.div>

          <div className="testimonials-carousel">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true, dynamicBullets: true }}
              autoplay={{ delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }}
              loop={testimonials.length > 3}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial) => {
                const platformInfo = getPlatformInfo(testimonial.source);
                const PlatformIcon = platformInfo.icon;
                return (
                  <SwiperSlide key={testimonial.id}>
                    <div className="testimonial-card">
                      <div className="testimonial-rating">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <FaStar key={i} />
                        ))}
                      </div>
                      <p className="testimonial-text">"{testimonial.testimonial}"</p>
                      <div className="testimonial-author">
                        <strong>{testimonial.customer_name}</strong>
                        {testimonial.verified && (
                          <span className={`platform-badge ${platformInfo.className}`}>
                            <PlatformIcon /> {platformInfo.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2>{ctaTitle}</h2>
            <p>{ctaDescription}</p>
            <div className="cta-actions">
              <button type="button" className="btn btn-primary btn-lg" onClick={openBookingModal}>
                {ctaPrimaryLabel}
              </button>
              <Link to="/contact" className="btn btn-outline btn-lg">
                {ctaSecondaryLabel}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
